import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Loader2, CheckCircle2, AlertCircle, MapPin, ArrowRight, Calendar, Clock, Lock } from "lucide-react";
import { TOURS, TID } from "@/data/site";
import { DatePicker, TimePicker } from "./DateTimePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mirror of backend PRICE_PACKAGES — only for the tour fixed-price preview.
const TOUR_PRICES = {
  "athens-city": 160,
  sounion: 200,
  corinth: 240,
  delphi: 360,
  "mycenae-nafplio-epidaurus": 400,
  meteora: 440,
  olympia: 440,
};

const Field = ({ label, required, children, hint }) => (
  <label className="block">
    <span className="block text-[0.72rem] uppercase tracking-[0.16em] text-foreground/65 mb-2">
      {label}
      {required && <span className="text-gold ml-1">*</span>}
    </span>
    {children}
    {hint && (
      <span className="block mt-1.5 text-xs text-foreground/50">{hint}</span>
    )}
  </label>
);

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-foreground placeholder:text-foreground/40 focus:outline-none focus:border-gold focus:bg-white/8 transition-colors";

const Stepper = ({ value, onChange, min = 0, max = 4, incTid, decTid, testId }) => (
  <div
    className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 px-3 py-2"
    data-testid={testId}
  >
    <button
      type="button"
      data-testid={decTid}
      onClick={() => onChange(Math.max(min, value - 1))}
      disabled={value <= min}
      className="stepper-btn"
      aria-label="Decrease"
    >
      <Minus className="h-4 w-4" />
    </button>
    <span className="font-display text-2xl text-foreground">{value}</span>
    <button
      type="button"
      data-testid={incTid}
      onClick={() => onChange(Math.min(max, value + 1))}
      disabled={value >= max}
      className="stepper-btn"
      aria-label="Increase"
    >
      <Plus className="h-4 w-4" />
    </button>
  </div>
);

const formatDate = (yyyymmdd) => {
  if (!yyyymmdd || !yyyymmdd.includes("-")) return yyyymmdd || "—";
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};
const formatTime = (hhmm) => hhmm || "—";

const Checkout = () => {
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null); // hero quote OR tour
  const [contact, setContact] = useState({
    name: "",
    email: "",
    phone: "",
    flight: "",
    passengers: 0,
    luggage: 0,
    notes: "",
    return_date: "",
    return_time: "",
    payment_method: "card_online",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  // 1) Hydrate trip from sessionStorage on mount. If nothing is there, send the
  //    customer back to the home page where they can use the quote widget.
  //    StrictMode double-invokes this effect in dev — useRef guard makes it idempotent.
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const heroRaw = sessionStorage.getItem("heroQuote");
    const pendingTour = sessionStorage.getItem("pendingService");

    // Stripe redirect (?session_id=…) brings the user back to /checkout. In
    // that case we keep the page rendered (it'll poll for payment) even
    // though we no longer have hero data — see the polling effect below.
    const params = new URLSearchParams(window.location.search);
    const hasStripeReturn = !!params.get("session_id");

    if (heroRaw) {
      try {
        const q = JSON.parse(heroRaw);
        setTrip({
          kind: "transfer",
          service: "custom-ride",
          pickup: q.pickup || "",
          dropoff: q.dropoff || "",
          pickup_lat: q.pickup_lat ?? null,
          pickup_lng: q.pickup_lng ?? null,
          dropoff_lat: q.dropoff_lat ?? null,
          dropoff_lng: q.dropoff_lng ?? null,
          date: q.date || "",
          time: q.time || "",
          is_return: !!q.is_return,
        });
        // Pre-fill return date/time on the checkout form if the user already
        // entered them on the hero quote — saves them from re-typing.
        if (q.is_return && (q.return_date || q.return_time)) {
          setContact((c) => ({
            ...c,
            return_date: q.return_date || "",
            return_time: q.return_time || "",
          }));
        }
        sessionStorage.removeItem("heroQuote");
        return;
      } catch {
        /* fall through */
      }
    }
    if (pendingTour) {
      const tour = TOURS.find((t) => t.id === pendingTour);
      if (tour) {
        setTrip({
          kind: "tour",
          service: tour.id,
          tour,
          pickup: "",
          dropoff: "Return to pickup",
          date: "",
          time: "",
          is_return: false,
        });
        sessionStorage.removeItem("pendingService");
        return;
      }
    }
    if (!hasStripeReturn) {
      // Nothing to checkout with — bounce back to home so the user can quote.
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // For tours we still need the customer's pickup address + date + time. Allow
  // the user to enter them right on the checkout page (transfers already have
  // them from the hero quote).
  const setTripField = (k, v) => setTrip((t) => (t ? { ...t, [k]: v } : t));

  // 2) Live price — tours have a static fixed price. For custom transfers the
  //    customer is NOT shown a price here — the owner emails them a quote
  //    after reviewing the request.
  const [pricing, setPricing] = useState(null); // { one_way_price, total_price, fixed_price }

  useEffect(() => {
    if (!trip) return;
    if (trip.kind === "tour") {
      const p = TOUR_PRICES[trip.service] || 0;
      setPricing({ one_way_price: p, total_price: p, fixed_price: true });
      return;
    }
    // Transfers: no live price lookup.
    setPricing(null);
  }, [trip]);

  // 3) Detect Stripe return and poll for payment confirmation.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const ref = params.get("ref");
    if (!sessionId) return;
    setStatus({ state: "polling", message: "Confirming your payment…" });
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus({
            state: "success",
            message: `Payment received! Booking #${ref || ""} confirmed. A confirmation email is on its way.`,
          });
          try {
            if (typeof window !== "undefined" && typeof window.gtag === "function") {
              const value = Number(r.data.amount) || 0;
              const currency = (r.data.currency || "eur").toUpperCase();
              window.gtag("event", "purchase", {
                transaction_id: ref || sessionId,
                value,
                currency,
                items: [
                  {
                    item_id: ref || sessionId,
                    item_name: "Athens Taxi Transfer",
                    price: value,
                    quantity: 1,
                  },
                ],
              });
            }
          } catch (_) {
            /* gtag optional */
          }
          window.history.replaceState({}, "", "/checkout");
          return;
        }
        if (r.data.status === "expired" || attempts >= maxAttempts) {
          setStatus({
            state: "error",
            message:
              "We couldn't confirm the payment yet. If you completed it, please contact us — your booking is saved.",
          });
          window.history.replaceState({}, "", "/checkout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts < maxAttempts) setTimeout(poll, 2000);
      }
    };
    poll();
  }, []);

  const totalAmount = useMemo(() => {
    if (!pricing) return null;
    return pricing.total_price ?? pricing.one_way_price ?? null;
  }, [pricing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!trip) return;

    // For tours we also need date + time and a pickup address.
    if (trip.kind === "tour") {
      if (!trip.pickup || !trip.date || !trip.time) {
        setStatus({ state: "error", message: "Please provide pickup address, date and time for your tour." });
        return;
      }
    }

    if (!contact.name || !contact.email || !contact.phone) {
      setStatus({ state: "error", message: "Please fill in your name, email and phone." });
      return;
    }
    if (!contact.passengers || contact.passengers < 1) {
      setStatus({ state: "error", message: "Please add at least 1 passenger." });
      return;
    }
    const pickupAt = new Date(`${trip.date}T${trip.time}:00`);
    const minPickup = new Date(Date.now() + 7 * 60 * 60 * 1000);
    if (Number.isNaN(pickupAt.getTime()) || pickupAt < minPickup) {
      const earliest = minPickup.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      setStatus({
        state: "error",
        message: `Bookings need at least 7 hours' notice. Earliest pickup right now: ${earliest}.`,
      });
      return;
    }
    if (trip.is_return) {
      if (!contact.return_date || !contact.return_time) {
        setStatus({ state: "error", message: "Please select return date and time." });
        return;
      }
      const returnAt = new Date(`${contact.return_date}T${contact.return_time}:00`);
      if (Number.isNaN(returnAt.getTime()) || returnAt <= pickupAt) {
        setStatus({ state: "error", message: "Return must be after the outbound pickup." });
        return;
      }
    }

    setStatus({ state: "loading", message: "Sending your booking request…" });
    try {
      const payload = {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        flight: contact.flight,
        date: trip.date,
        time: trip.time,
        pickup: trip.pickup,
        stop: "",
        dropoff: trip.dropoff || (trip.kind === "tour" ? "Return to pickup" : ""),
        service: trip.service,
        passengers: contact.passengers,
        luggage: contact.luggage,
        notes: contact.notes,
        payment_method: contact.payment_method,
        is_return: !!trip.is_return,
        return_date: trip.is_return ? contact.return_date : "",
        return_time: trip.is_return ? contact.return_time : "",
        distance_km: pricing?.distance_km ?? null,
        pickup_lat: trip.pickup_lat,
        pickup_lng: trip.pickup_lng,
        dropoff_lat: trip.dropoff_lat,
        dropoff_lng: trip.dropoff_lng,
      };
      const res = await axios.post(`${API}/bookings`, payload);
      try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          const value = Number(totalAmount) || 0;
          window.gtag("event", "generate_lead", {
            currency: "EUR",
            value,
            items: [
              {
                item_id: res.data.reference,
                item_name: trip.service || "Athens Taxi Transfer",
                price: value,
                quantity: 1,
              },
            ],
          });
        }
      } catch (_) {
        /* gtag optional */
      }
      // Clear the pending trip so a browser back-navigation doesn't re-submit.
      try {
        sessionStorage.removeItem("aat_trip");
      } catch (_) {
        /* sessionStorage may be unavailable */
      }
      // Manual confirmation flow (as before): no card taken at submission
      // time. The owner receives an email with Confirm/Decline; on Confirm,
      // the customer receives a Stripe payment link.
      setStatus({
        state: "success",
        message: `Thanks — we've received your booking request (ref ${res.data.reference}). Our team is checking driver availability and will email you a secure Stripe payment link once your booking is confirmed. You have not been charged.`,
      });
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data;
      let safeDetail = "";
      if (status < 500 && typeof d !== "string" && typeof d?.detail === "string") {
        const low = d.detail.toLowerCase();
        const infraTokens = ["cloudflare", "origin web server", "bad gateway", "nginx", "<!doctype", "<html", "502", "503", "504"];
        if (!infraTokens.some((t) => low.includes(t))) safeDetail = d.detail;
      }
      const msg =
        safeDetail ||
        "Something went wrong. Please try WhatsApp or email instead.";
      setStatus({ state: "error", message: msg });
    }
  };

  // Confirmation card — rendered as soon as the booking request is sent
  // (or when the Stripe payment page returns here without a `trip` in state).
  if (status.state === "success" || (!trip && (status.state === "polling" || status.state === "error"))) {
    return (
      <section data-testid="checkout-result" className="section">
        <div className="max-w-2xl mx-auto px-5 lg:px-10 text-center">
          {status.state === "success" && (
            <div className="rounded-3xl glass-strong p-8">
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-300" />
              <div className="mt-4 font-display text-3xl text-gold">Request received</div>
              <p className="mt-3 text-sm text-foreground/70 leading-relaxed">
                {status.message}
              </p>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold text-[#0a1830] px-6 py-3 font-semibold text-sm hover:bg-[#f0c878] transition-colors"
              >
                Back to home <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
          {status.state === "polling" && (
            <div className="rounded-3xl glass-strong p-8">
              <Loader2 className="mx-auto h-12 w-12 text-gold animate-spin" />
              <div className="mt-4 font-display text-2xl text-foreground">{status.message}</div>
            </div>
          )}
          {status.state === "error" && (
            <div className="rounded-3xl glass-strong p-8">
              <AlertCircle className="mx-auto h-12 w-12 text-red-300" />
              <div className="mt-4 font-display text-2xl text-foreground">Could not send request</div>
              <p className="mt-3 text-sm text-foreground/70">{status.message}</p>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold text-[#0a1830] px-6 py-3 font-semibold text-sm hover:bg-[#f0c878] transition-colors"
              >
                Back to home <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </section>
    );
  }

  if (!trip) return null; // brief moment before the redirect kicks in

  return (
    <section id="booking" data-testid={TID.bookingSection} className="section">
      <div className="max-w-3xl mx-auto px-5 lg:px-10">
        <div className="text-center mb-8">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-xs uppercase tracking-[0.22em] text-foreground/55 hover:text-gold transition-colors mb-4"
          >
            ← Back to quote
          </button>
          <h1 className="font-display text-3xl md:text-4xl text-gold">
            {trip.kind === "tour" ? "Complete your tour booking" : "Complete your booking"}
          </h1>
          <p className="mt-2 text-sm text-foreground/65">
            A few more details and your driver is reserved.
          </p>
        </div>

        {/* Trip summary card */}
        <div
          data-testid="trip-summary"
          className="rounded-3xl glass-strong p-5 lg:p-6 mb-6"
        >
          {trip.kind === "tour" ? (
            <>
              <div className="text-[0.7rem] uppercase tracking-[0.2em] text-gold mb-1.5">
                Selected tour
              </div>
              <div className="font-display text-2xl text-foreground leading-tight">
                {trip.tour.title}
              </div>
              <div className="mt-1 text-xs text-foreground/65">
                {trip.tour.duration} · round trip from your pickup
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <Field label="Pickup address" required>
                  <input
                    data-testid="checkout-tour-pickup"
                    className={inputCls}
                    value={trip.pickup}
                    onChange={(e) => setTripField("pickup", e.target.value)}
                    placeholder="Hotel, address, port…"
                  />
                </Field>
                <Field label="Pickup date" required>
                  <DatePicker
                    testId="checkout-tour-date"
                    value={trip.date}
                    onChange={(v) => setTripField("date", v)}
                  />
                </Field>
                <Field label="Pickup time" required>
                  <TimePicker
                    testId="checkout-tour-time"
                    value={trip.time}
                    onChange={(v) => setTripField("time", v)}
                  />
                </Field>
              </div>
            </>
          ) : (
            <>
              <div className="text-[0.7rem] uppercase tracking-[0.2em] text-foreground/55 mb-3">
                {trip.is_return ? "Return transfer" : "One-way transfer"}
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                <div className="text-sm text-foreground leading-snug">
                  <div className="text-foreground/55 text-[0.7rem] uppercase tracking-[0.16em]">From</div>
                  <div>{trip.pickup}</div>
                </div>
              </div>
              <div className="ml-2.5 my-1 border-l border-dashed border-white/20 h-5" />
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                <div className="text-sm text-foreground leading-snug">
                  <div className="text-foreground/55 text-[0.7rem] uppercase tracking-[0.16em]">To</div>
                  <div>{trip.dropoff}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <Calendar className="h-4 w-4 text-gold" />
                  <span>{formatDate(trip.date)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <Clock className="h-4 w-4 text-gold" />
                  <span>{formatTime(trip.time)}</span>
                </div>
              </div>
            </>
          )}

          <div className="mt-5 pt-4 border-t border-white/10">
            {trip.kind === "tour" ? (
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.18em] text-foreground/65">
                  Tour price
                </div>
                <div className="font-display text-3xl text-gold leading-none">
                  {totalAmount != null ? `€${totalAmount}` : "—"}
                </div>
              </div>
            ) : (
              <div className="text-xs text-foreground/70 leading-relaxed">
                Submit your request below. Our team will review your trip and email you a <strong className="text-gold">quote</strong> plus a secure Stripe payment link. <strong>You have not been charged.</strong>
              </div>
            )}
          </div>
        </div>

        {/* Contact form */}
        <form
          onSubmit={submit}
          data-testid={TID.bookingForm}
          className="rounded-3xl glass-strong p-5 lg:p-7"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Full name" required>
              <input
                data-testid={TID.bookingName}
                required
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                className={inputCls}
                placeholder="John Smith"
              />
            </Field>
            <Field label="Email" required>
              <input
                data-testid={TID.bookingEmail}
                required
                type="email"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                className={inputCls}
                placeholder="you@email.com"
              />
            </Field>
            <Field label="Phone / WhatsApp" required>
              <input
                data-testid={TID.bookingPhone}
                required
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                className={inputCls}
                placeholder="+44 7…"
              />
            </Field>
            {trip.kind !== "tour" && (
              <Field label="Flight number / Ship name">
                <input
                  data-testid={TID.bookingFlight}
                  value={contact.flight}
                  onChange={(e) => setContact({ ...contact, flight: e.target.value })}
                  className={inputCls}
                  placeholder="e.g. A3 600, Blue Star Patmos"
                />
              </Field>
            )}
            <div className={`md:col-span-2 grid ${trip.kind === "tour" ? "grid-cols-1" : "grid-cols-2"} gap-4`}>
              <Field label="Passengers" required hint="Max 4">
                <Stepper
                  testId={TID.bookingPassengers}
                  value={contact.passengers}
                  onChange={(v) => setContact({ ...contact, passengers: v })}
                  min={0}
                  max={4}
                  incTid={TID.passengersInc}
                  decTid={TID.passengersDec}
                />
              </Field>
              {trip.kind !== "tour" && (
                <Field label="Luggage" required hint="Max 4">
                  <Stepper
                    testId={TID.bookingLuggage}
                    value={contact.luggage}
                    onChange={(v) => setContact({ ...contact, luggage: v })}
                    min={0}
                    max={4}
                    incTid={TID.luggageInc}
                    decTid={TID.luggageDec}
                  />
                </Field>
              )}
            </div>

            {/* Return date/time — only when the customer chose Return at the hero */}
            {trip.is_return && (
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5 rounded-2xl bg-gold/5 border border-gold/20 px-4 py-4">
                <Field label="Return date" required>
                  <DatePicker
                    testId="booking-return-date"
                    value={contact.return_date}
                    onChange={(v) => setContact({ ...contact, return_date: v })}
                    minDate={trip.date || undefined}
                  />
                </Field>
                <Field label="Return time" required>
                  <TimePicker
                    testId="booking-return-time"
                    value={contact.return_time}
                    onChange={(v) => setContact({ ...contact, return_time: v })}
                  />
                </Field>
              </div>
            )}

            <div className="md:col-span-2">
              <Field label="Notes">
                <textarea
                  data-testid={TID.bookingNotes}
                  value={contact.notes}
                  onChange={(e) => setContact({ ...contact, notes: e.target.value })}
                  rows={3}
                  className={inputCls + " resize-none"}
                />
              </Field>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-4">
            <div className="rounded-xl border border-gold/30 bg-gold/10 p-4 flex items-start gap-3">
              <Lock className="h-5 w-5 shrink-0 mt-0.5 text-gold" />
              <div className="text-[0.82rem] leading-relaxed text-foreground/85">
                <strong className="text-gold">Online payment by card via Stripe.</strong> We'll reply very fast with your confirmation and the secure payment link — no charge is made until then.
              </div>
            </div>

            {status.state === "error" && (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/12 border border-red-400/30 text-red-200 text-sm">
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                {status.message}
              </div>
            )}
            {status.state === "polling" && (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-500/12 border border-blue-400/30 text-blue-200 text-sm">
                <Loader2 className="h-5 w-5 shrink-0 mt-0.5 animate-spin" />
                {status.message}
              </div>
            )}

            <button
              type="submit"
              data-testid={TID.bookingSubmit}
              disabled={status.state === "loading" || status.state === "polling" || (trip?.kind === "tour" && !pricing)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gold text-[#0a1830] px-7 py-4 font-semibold text-sm hover:bg-[#f0c878] transition-colors disabled:opacity-60"
            >
              {status.state === "loading" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {status.message || "Sending…"}
                </>
              ) : (
                "Request booking"
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Checkout;
